import React, { useState, useMemo, useEffect } from 'react';
import { Button, Modal, Select, Checkbox, Alert, Space, Typography } from 'antd';
import { useApolloClient } from 'react-apollo';
import { ALL_BLUEPRINTS, getRecommendedBlueprint, resolveSegment, getStrategySetOptions } from '../../../../core/strategyBlueprint/blueprints';
import { useTimezone } from '../../../../core/hooks/useTimezone';
import { toStartOfDayUtc, toEndOfDayUtc } from '../../CampaignRequestor/utils/dateNormalization';
import GET_NEXXEN_PUBLISHER_DEALS from '../../../../core/GraphQl/Queries/GET_NEXXEN_PUBLISHER_DEALS';
import GET_DSP_INVENTORY_LISTS from '../../../../core/GraphQl/Queries/GET_DSP_INVENTORY_LISTS';

const { Text } = Typography;

// Module-level helpers — no component state needed
function resolveBlueprint(key) {
  return ALL_BLUEPRINTS.find((b) => b.key === key) || null;
}

function getOptionsForBlueprint(blueprintKey) {
  const bp = resolveBlueprint(blueprintKey);
  return getStrategySetOptions(bp);
}

function buildEnabledItems(set) {
  if (!set || !set.lineItems) return {};
  const map = {};
  set.lineItems.forEach((_, i) => { map[i] = true; });
  return map;
}

function computeItemBudget(set, enabledItems, sourceBudget, blueprintIndex) {
  const enabledWeight = set.lineItems.reduce(
    (sum, bi, i) => (enabledItems[i] ? sum + (Number(bi.budgetAllocation) || 0) : sum), 0,
  );
  if (enabledWeight === 0) return 0;
  const weight = Number(set.lineItems[blueprintIndex].budgetAllocation) || 0;
  return Math.round((weight / enabledWeight) * sourceBudget * 100) / 100;
}

// Fetch all Nexxen publisher deals (paginated) so blueprint deals can be resolved to
// their numeric Nexxen internal ID (dspInternalId). Mirrors CampaignHeader's fetch.
// External-service requires a numeric dspInternalId; without it, blueprint deals are
// skipped at the DSP and have to be set manually after launch.
async function fetchPublisherDeals(apolloClient) {
  const allDeals = [];
  let offset = 0;
  const limit = 100;
  try {
    for (;;) {
      const { data } = await apolloClient.query({
        query: GET_NEXXEN_PUBLISHER_DEALS,
        variables: { forceRefresh: false, offset, limit },
        fetchPolicy: 'cache-first',
      });
      const pageData = (data && data.nexxenPublisherDeals && data.nexxenPublisherDeals.data) || [];
      const total = (data && data.nexxenPublisherDeals && data.nexxenPublisherDeals.queryTotal) || 0;
      allDeals.push(...pageData);
      if (allDeals.length >= total || pageData.length === 0) break;
      offset += limit;
    }
  } catch (error) {
    console.warn('Failed to fetch Nexxen publisher deals for blueprint apply:', error);
  }
  // Deduplicate by dealId
  const seen = new Set();
  return allDeals.filter((d) => {
    if (!d || !d.dealId || seen.has(d.dealId)) return false;
    seen.add(d.dealId);
    return true;
  });
}

// Fetch DSP inventory lists so blueprint contextual targets can be resolved to their
// listId. inventoryLists are stored on line items and saved as a relation via
// useCampaignSaveManager (same as selecting them in the Inventory dialog).
async function fetchInventoryLists(apolloClient) {
  try {
    const { data } = await apolloClient.query({
      query: GET_DSP_INVENTORY_LISTS,
      variables: { forceRefresh: false },
      fetchPolicy: 'cache-first',
    });
    return (data && data.dspInventoryLists && data.dspInventoryLists.data) || [];
  } catch (error) {
    console.warn('Failed to fetch DSP inventory lists for blueprint apply:', error);
    return [];
  }
}

export function StrategyBlueprintModal({ form, campaignIndex, currentCampaignOrder, visible, onClose, onFormUpdated }) {
  const { timezone } = useTimezone();
  const apolloClient = useApolloClient();
  const [applying, setApplying] = useState(false);
  const [assignments, setAssignments] = useState({});
  const [markOriginals, setMarkOriginals] = useState(true);

  const advertiser = currentCampaignOrder?.advertiser;
  const parentOrgs = Array.isArray(advertiser?.parentOrg) ? advertiser.parentOrg : advertiser?.parentOrg ? [advertiser.parentOrg] : [];
  const channelPartnerName = parentOrgs.map((p) => p.name).join(' ') || advertiser?.name || '';
  const cpAcronym = parentOrgs.map((p) => p.CPAcronym).filter(Boolean).join(' ') || advertiser?.CPAcronym || '';
  const campaignBudget = currentCampaignOrder?.campaigns?.[campaignIndex]?.budgetTotal || currentCampaignOrder?.budget || 0;

  const recommendedBlueprint = useMemo(
    () => getRecommendedBlueprint(channelPartnerName, cpAcronym, campaignBudget),
    [channelPartnerName, cpAcronym, campaignBudget],
  );

  const originalLineItems = useMemo(() => {
    const items = form.getFieldValue(['campaigns', campaignIndex, 'lineItems']) || [];
    return items;
  }, [form, campaignIndex]);

  // Reset assignments when modal opens — each item gets its own blueprint key
  useEffect(() => {
    if (visible) {
      const defaultKey = recommendedBlueprint?.key || ALL_BLUEPRINTS[0]?.key || null;
      const defaultOpts = getOptionsForBlueprint(defaultKey);
      const defaultBp = resolveBlueprint(defaultKey);
      const defaultSet = defaultBp?.strategySets?.[defaultOpts[0]?.key];
      const initial = {};
      originalLineItems.forEach((_, idx) => {
        initial[idx] = { blueprintKey: defaultKey, strategyKey: defaultOpts[0]?.key || null, skip: false, enabledItems: buildEnabledItems(defaultSet) };
      });
      setAssignments(initial);
      setMarkOriginals(true);
    }
  }, [visible, originalLineItems.length, originalLineItems, recommendedBlueprint]);

  const totals = useMemo(() => {
    let totalItems = 0;
    originalLineItems.forEach((li, idx) => {
      const assignment = assignments[idx];
      if (!assignment || assignment.skip || !assignment.strategyKey || !assignment.blueprintKey) return;
      const bp = resolveBlueprint(assignment.blueprintKey);
      if (!bp) return;
      const set = bp.strategySets[assignment.strategyKey];
      if (!set) return;
      const enabled = assignment.enabledItems || {};
      totalItems += set.lineItems.filter((_, i) => enabled[i] !== false).length;
    });
    return { totalItems, totalBudget: campaignBudget };
  }, [assignments, originalLineItems, campaignBudget]);

  const handleApply = async () => {
    setApplying(true);
    // Use the campaign's budget as the total pool
    const budgetField = form.getFieldValue(['campaigns', campaignIndex, 'budget']);
    const campaignBudget = Number(budgetField) || 0;

    // Resolve each blueprint deal to its numeric Nexxen internal ID (dspInternalId).
    // Match by dealId first, then fall back to the numeric internal id. Some deal IDs
    // are passed as the numeric internal id itself (e.g. 397100923868) while others are
    // string deal IDs (e.g. PM-WETW-6925).
    const publisherDeals = await fetchPublisherDeals(apolloClient);
    const dealLookup = new Map(publisherDeals.map((d) => [d.dealId, d]));
    const dealIdByInternalId = new Map(publisherDeals.map((d) => [String(d.id), d]));

    // Resolve blueprint contextual targets to DSP inventory lists. These are attached
    // to line items as inventoryLists (same shape as CampaignRequestorInventoryDialog).
    const inventoryLists = await fetchInventoryLists(apolloClient);
    const inventoryListLookup = new Map(inventoryLists.map((l) => [l.name, l]));
    const inventoryListById = new Map(inventoryLists.map((l) => [String(l.id), l]));

    const allClones = [];
    const unresolvedDeals = [];
    const unresolvedContextualTargets = [];

    originalLineItems.forEach((sourceItem, idx) => {
      const assignment = assignments[idx];
      if (!assignment || assignment.skip || !assignment.strategyKey || !assignment.blueprintKey) return;

      const blueprint = resolveBlueprint(assignment.blueprintKey);
      if (!blueprint) return;

      const set = blueprint.strategySets[assignment.strategyKey];
      if (!set) return;

      const campaignStartDate = form.getFieldValue(['campaigns', campaignIndex, 'startDate']);
      const campaignEndDate = form.getFieldValue(['campaigns', campaignIndex, 'endDate']);

      // Only iterate enabled blueprint items
      const enabledItems = assignment.enabledItems || {};
      const enabledIndices = set.lineItems.map((_, i) => i).filter((i) => enabledItems[i] !== false);
      if (enabledIndices.length === 0) return;

      // Calculate total weight over enabled items only
      const setTotalWeight = enabledIndices.reduce(
        (sum, i) => sum + (Number(set.lineItems[i].budgetAllocation) || 0), 0,
      );
      const sourceBudget = Number(sourceItem.budgetTotal) || 0;

      let setAllocated = 0;

      enabledIndices.forEach((blueprintIndex, enabledPosition) => {
        const blueprintItem = set.lineItems[blueprintIndex];
        const clone = JSON.parse(JSON.stringify(sourceItem));
        delete clone.id;

        clone.name = `${blueprintItem.name}_${sourceItem.name}`;
        // Clear inherited budgetPercentage — the clone's budgetTotal no longer matches
        // the source's percentage. Leaving it stale causes onBlur to overwrite budgetTotal.
        clone.budgetPercentage = null;
        // Use source line item's budget as the pool, distributed by relative weight
        const weight = Number(blueprintItem.budgetAllocation) || 0;
        clone.budgetTotal = setTotalWeight > 0
          ? Math.round((weight / setTotalWeight) * sourceBudget * 100) / 100
          : 0;

        // Fix rounding on last enabled clone so the set sums exactly to source budget
        if (enabledPosition === enabledIndices.length - 1 && setTotalWeight > 0) {
          clone.budgetTotal = Math.round((sourceBudget - setAllocated) * 100) / 100;
        }
        setAllocated += clone.budgetTotal;

        // Recalculate budgetPercentage from the new budgetTotal so it stays consistent
        if (campaignBudget > 0 && clone.budgetTotal > 0) {
          clone.budgetPercentage = parseFloat((clone.budgetTotal / campaignBudget * 100).toFixed(1));
        }

        clone.isOrigin = false;
        clone.markedForDelivery = true;
        clone.valueAdd = false;
        clone.dspLineItemId = null;

        // Clean up nested IDs — same as manual clone button
        // Without this, all blueprint clones share the same adGroup/media records
        delete clone.adProviderId;
        if (clone.adGroup && clone.adGroup.id) {
          delete clone.adGroup.id;
          if (clone.adGroup.adGroupCreatives && clone.adGroup.adGroupCreatives.length) {
            clone.adGroup.adGroupCreatives = clone.adGroup.adGroupCreatives.map((agc) => {
              delete agc.id;
              delete agc.adProviderId;
              // Re-hydrate creative dates (JSON.stringify turned Moments into strings)
              agc.startDate = toStartOfDayUtc(agc.startDate, timezone);
              agc.endDate = toEndOfDayUtc(agc.endDate, timezone);
              if (agc.medias && agc.medias.length) {
                agc.medias = agc.medias.map((media) => {
                  delete media.id;
                  delete media.dspCreativeId;
                  delete media.dspAdId;
                  return media;
                });
              }
              return agc;
            });
          }
        }
        if (clone.firstPartyDataAudiences && clone.firstPartyDataAudiences.length) {
          clone.firstPartyDataAudiences.forEach((a) => delete a.id);
        }
        if (clone.targetingRegions && clone.targetingRegions.length) {
          clone.targetingRegions.forEach((r) => delete r.id);
        }
        if (clone.targetingLocations && clone.targetingLocations.length) {
          clone.targetingLocations.forEach((l) => delete l.id);
        }

        // Re-hydrate flight schedule dates: JSON.stringify turned Moment objects into strings.
        // AntD DatePicker expects Moment objects, so we normalize them back.
        const rawFlights = clone.flightSchedule || clone.flightSchedules;
        if (Array.isArray(rawFlights) && rawFlights.length > 0) {
          clone.flightSchedule = rawFlights.map((flight) => ({
            ...flight,
            id: undefined,
            startDate: toStartOfDayUtc(flight.startDate, timezone),
            endDate: toEndOfDayUtc(flight.endDate, timezone),
          }));
          delete clone.flightSchedules;

          // Scale flight schedule budgets to match the new line-item budget
          const sourceBudget = Number(sourceItem.budgetTotal) || 0;
          const cloneBudget = Number(clone.budgetTotal) || 0;
          if (sourceBudget > 0 && clone.flightSchedule.length > 0) {
            const scaleRatio = cloneBudget / sourceBudget;
            clone.flightSchedule = clone.flightSchedule.map((flight) => ({
              ...flight,
              budget: Math.round((Number(flight.budget) || 0) * scaleRatio * 100) / 100,
            }));

            // Fix rounding: ensure flights sum exactly to clone budget
            const flightsTotal = clone.flightSchedule.reduce((sum, f) => sum + (Number(f.budget) || 0), 0);
            const gap = Math.round((cloneBudget - flightsTotal) * 100) / 100;
            if (gap !== 0) {
              const lastFlight = clone.flightSchedule[clone.flightSchedule.length - 1];
              lastFlight.budget = Math.round(((Number(lastFlight.budget) || 0) + gap) * 100) / 100;
            }
          }
        }

        if (campaignStartDate) clone.startDate = campaignStartDate;
        if (campaignEndDate) clone.endDate = campaignEndDate;

        const segment = resolveSegment(blueprintItem.segment);
        if (segment) {
          // Use the FORM-state field `nexxenSegments` (not `segments`).
          // The line-item UI table, the Nexxen segments dialog, and the save manager
          // (useCampaignSaveManager) all read/write `nexxenSegments`; the save manager
          // transforms it into the GraphQL `segments` relation. Writing `segments` here
          // previously caused blueprint segments to be silently dropped on save (and any
          // existing segments deleted), so they never reached the DSP and had to be set
          // manually after launch.
          clone.nexxenSegments = [{ segmentId: segment.segmentId, name: segment.name, segmentType: segment.segmentType, cost: segment.cost }];
        }

        // Use the strategy-set's deals if defined, otherwise fall back to the
        // blueprint-level deals. This lets a single blueprint have different deal
        // sets for prospecting vs conquesting (or other strategy variants).
        const deals = set.deals || blueprint.deals || [];

        // Use the per-line-item blueprint's deals, resolved to their Nexxen internal
        // ID (dspInternalId) via the publisher-deals lookup fetched above.
        clone.dspDealIds = deals.map((d) => {
          const info = dealLookup.get(d.dealId) || dealIdByInternalId.get(d.dealId);
          if (!info) unresolvedDeals.push(d.dealId);
          return {
            dspInternalId: (info && info.id) || null,
            dealId: (info && info.dealId) || d.dealId,
            name: (info && info.name) || d.name,
            cpmBid: (info && info.price) || null,
          };
        });

        // Apply blueprint contextual targets as inventoryLists. If the blueprint
        // provides a listId, trust it and attach it directly without requiring an
        // API lookup. Some inventory lists are valid in the DSP but are not returned
        // by getDspInventoryLists, so strict validation here creates false warnings.
        // Otherwise, resolve by name and warn only if we cannot find the list at all.
        // The save manager expects { listId, name, action, inventoryUsage }.
        if (blueprintItem.contextualTarget) {
          const targetName = typeof blueprintItem.contextualTarget === 'string'
            ? blueprintItem.contextualTarget
            : blueprintItem.contextualTarget.name;
          const targetListId = typeof blueprintItem.contextualTarget === 'object'
            ? String(blueprintItem.contextualTarget.listId || '')
            : '';
          const listInfo = (targetListId && inventoryListById.get(targetListId))
            || inventoryListLookup.get(targetName);
          // Only warn when we have neither a provided listId nor a resolved list.
          if (!targetListId && !listInfo) unresolvedContextualTargets.push(targetName);
          clone.inventoryLists = [
            {
              listId: targetListId || (listInfo && listInfo.id) || null,
              name: targetName,
              action: 'include',
              inventoryUsage: (listInfo && listInfo.inventoryUsage) || 'sites',
            },
          ];
        }

        allClones.push(clone);
      });
    });

    // Inject into form state (same pattern as Nexxen sync)
    const currentLineItems = form.getFieldValue(['campaigns', campaignIndex, 'lineItems']) || [];

    const updatedLineItems = currentLineItems.map((li, idx) => {
      const assignment = assignments[idx];
      const hadStrategyApplied = assignment && !assignment.skip && !!assignment.strategyKey;
      return {
        ...li,
        // Only mark as reference if user checked the box AND this item had a strategy applied.
        // Skipped items retain their original markedForDelivery status.
        markedForDelivery: (markOriginals && hadStrategyApplied) ? false : li.markedForDelivery,
      };
    });

    form.setFieldValue(
      ['campaigns', campaignIndex, 'lineItems'],
      [...updatedLineItems, ...allClones],
    );

    setApplying(false);
    if (onFormUpdated) onFormUpdated();
    onClose();

    // Surface any contextual targets that couldn't be resolved to a DSP inventory list.
    // Without listId the target can't be saved/applied at the DSP, so warn the user.
    if (unresolvedContextualTargets.length > 0) {
      const uniqueUnresolved = Array.from(new Set(unresolvedContextualTargets));
      Modal.warning({
        title: 'Some contextual targets could not be resolved',
        width: 520,
        content: (
          <div>
            <p>
              These contextual targets were applied but don&rsquo;t exist in the DSP inventory lists, so they
              have no numeric list ID (<Text code>listId</Text>). They will be{' '}
              <strong>silently skipped at launch</strong>:
            </p>
            <ul style={{ maxHeight: 220, overflowY: 'auto', paddingLeft: 20 }}>
              {uniqueUnresolved.map((target) => (
                <li key={target}>
                  <Text code>{target}</Text>
                </li>
              ))}
            </ul>
            <p>Confirm they are valid inventory list names, or remove them from the blueprint line items.</p>
          </div>
        ),
      });
    }

    // Surface any deals that couldn't be resolved to a Nexxen internal ID. They are
    // still attached in the dashboard, but the DSP silently rejects non-numeric
    // dspInternalId at launch — so warn the user to fix/remove them before launching.
    if (unresolvedDeals.length > 0) {
      const uniqueUnresolved = Array.from(new Set(unresolvedDeals));
      Modal.warning({
        title: 'Some deals could not be resolved to a Nexxen ID',
        width: 520,
        content: (
          <div>
            <p>
              These deals were applied but don&rsquo;t exist in Nexxen&rsquo;s publisher deals, so they have no
              numeric Nexxen ID (<Text code>dspInternalId</Text>). They will be{' '}
              <strong>silently skipped at launch</strong> and won&rsquo;t reach the DSP:
            </p>
            <ul style={{ maxHeight: 220, overflowY: 'auto', paddingLeft: 20 }}>
              {uniqueUnresolved.map((dealId) => (
                <li key={dealId}>
                  <Text code>{dealId}</Text>
                </li>
              ))}
            </ul>
            <p>Remove them from the line items before launching, or confirm they are valid Nexxen deals.</p>
          </div>
        ),
      });
    }
  };

  return (
    <Modal
      title="Apply Strategy Blueprint"
      visible={visible}
      onCancel={onClose}
      width={780}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="apply"
          type="primary"
          onClick={handleApply}
          loading={applying}
          disabled={totals.totalItems === 0}
        >
          Apply Strategy ({totals.totalItems} items)
        </Button>,
      ]}
    >
      <Alert
        message="Assign a blueprint and strategy set to each line item. New items are added to the form — click Save to persist."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {originalLineItems.map((li, idx) => {
        const assignment = assignments[idx] || {};
        const budget = Number(li.budgetTotal) || 0;
        const itemBlueprint = resolveBlueprint(assignment.blueprintKey);
        const itemStrategyOptions = getOptionsForBlueprint(assignment.blueprintKey);
        const set = assignment.strategyKey && itemBlueprint ? itemBlueprint.strategySets[assignment.strategyKey] : null;
        const enabledItems = assignment.enabledItems || {};
        const enabledCount = set ? set.lineItems.filter((_, i) => enabledItems[i] !== false).length : 0;
        const itemCount = assignment.skip ? 0 : enabledCount;
        const allUnchecked = set && enabledCount === 0;

        return (
          <div
            key={idx}
            style={{
              padding: 12,
              border: '1px solid #f0f0f0',
              borderRadius: 8,
              marginBottom: 12,
              opacity: assignment.skip ? 0.5 : 1,
            }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text strong>{li.name || `Line Item ${idx + 1}`}</Text>
                <Text type="secondary">${budget.toLocaleString()}</Text>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Select
                  value={assignment.blueprintKey}
                  onChange={(val) => {
                    const newOpts = getOptionsForBlueprint(val);
                    const newBp = resolveBlueprint(val);
                    const newSet = newBp?.strategySets?.[newOpts[0]?.key];
                    setAssignments((prev) => ({
                      ...prev,
                      [idx]: { ...prev[idx], blueprintKey: val, strategyKey: newOpts[0]?.key || null, skip: false, enabledItems: buildEnabledItems(newSet) },
                    }));
                  }}
                  disabled={assignment.skip}
                  style={{ minWidth: 220 }}
                >
                  {ALL_BLUEPRINTS.map((bp) => (
                    <Select.Option key={bp.key} value={bp.key}>
                      {bp.label}
                    </Select.Option>
                  ))}
                </Select>
                <Select
                  value={assignment.strategyKey}
                  onChange={(val) => {
                    const bp = resolveBlueprint(assignment.blueprintKey);
                    const newSet = bp?.strategySets?.[val];
                    setAssignments((prev) => ({
                      ...prev,
                      [idx]: { ...prev[idx], strategyKey: val, skip: false, enabledItems: buildEnabledItems(newSet) },
                    }));
                  }}
                  disabled={assignment.skip}
                  style={{ minWidth: 220 }}
                >
                  {itemStrategyOptions.map((opt) => (
                    <Select.Option key={opt.key} value={opt.key}>
                      {opt.label}
                    </Select.Option>
                  ))}
                </Select>
                <Checkbox
                  checked={assignment.skip}
                  onChange={(e) =>
                    setAssignments((prev) => ({
                      ...prev,
                      [idx]: { ...prev[idx], skip: e.target.checked },
                    }))
                  }
                >
                  Skip
                </Checkbox>
              </div>
              {!assignment.skip && set && set.lineItems.length > 0 && (
                <div style={{ borderLeft: '3px solid #1890ff', paddingLeft: 10, marginTop: 4 }}>
                  {set.lineItems.map((blueprintItem, bi) => {
                    const isEnabled = enabledItems[bi] !== false;
                    const itemBudget = isEnabled ? computeItemBudget(set, enabledItems, budget, bi) : 0;
                    return (
                      <div key={bi} style={{ display: 'flex', alignItems: 'center', padding: '2px 0' }}>
                        <Checkbox
                          checked={isEnabled}
                          onChange={() => {
                            setAssignments((prev) => ({
                              ...prev,
                              [idx]: {
                                ...prev[idx],
                                enabledItems: { ...prev[idx].enabledItems, [bi]: !isEnabled },
                              },
                            }));
                          }}
                        >
                          <span style={!isEnabled ? { opacity: 0.4 } : {}}>
                            {blueprintItem.name}
                          </span>
                        </Checkbox>
                        {isEnabled && (
                          <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                            ${itemBudget.toLocaleString()}
                          </Text>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {allUnchecked && !assignment.skip && (
                <Alert
                  message="Enable at least one item or skip this line item."
                  type="warning"
                  showIcon
                  style={{ marginTop: 4 }}
                />
              )}
              {!assignment.skip && itemCount > 0 && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Will create {itemCount} line items totaling ${budget.toLocaleString()}
                </Text>
              )}
            </Space>
          </div>
        );
      })}

      <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 12, marginTop: 8 }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Text strong>
            Total: {totals.totalItems} items, ${totals.totalBudget.toLocaleString()}
          </Text>
          <Checkbox checked={markOriginals} onChange={(e) => setMarkOriginals(e.target.checked)}>
            Mark originals as reference-only
          </Checkbox>
        </Space>
      </div>
    </Modal>
  );
}
